import { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { PageHead } from "@/components/PageHead";
import EnvironmentBadge from "@/components/EnvironmentBadge";
import fsvLogo from "@/images/achieveos-logo.png";
import fsvLogoDark from "@/images/achieveos-logo-dark.png";
import { StarPortfolioForm } from "./components/StarPortfolioForm";
import { MarketingPanel } from "./components/MarketingPanel";
import { StepProgress } from "@/components/StepProgress";
import { profileStepCompleted } from "@/utils/tracking";
import { useAuth } from "@/hooks/useAuth";
import { Roles } from "@/constants/roles";
import { getPrimaryRole, hasRole } from "@/utils/roleUtils";
import { ADVISOR_STEP4_PANEL_CONTENT } from "@/constants/signupPanelContent";
import { EXPERT_SIGNUP_STEP_LABELS } from "@/constants/signupSteps";
import { ThemeToggle } from "@/components/ThemeToggle";

const SignUpStepFour = () => {
  const { user } = useAuth();
  const primaryRole = user ? getPrimaryRole(user) : null;
  const isAdvisor = hasRole(user, Roles.advisor);

  useEffect(() => {
    if (primaryRole) {
      profileStepCompleted({ step_name: "star_portfolio_viewed", user_role: primaryRole });
    }
  }, [primaryRole]);

  // signup/4 (S.T.A.R. Portfolio) is the Expert flow's last step. Achievers
  // finish at signup/3 and never route here, so a non-advisor landing on this
  // URL is sent on rather than shown an Expert-only step.
  const renderForm = () => {
    if (!primaryRole) return null;
    if (isAdvisor) return <StarPortfolioForm />;
    return <Navigate to="/home" replace />;
  };

  return (
    <>
      <PageHead title="Expert Sign Up: STAR Portfolio" description="Create your AchieveOS account" />
      <EnvironmentBadge />

      <div className="fixed inset-0 flex flex-col overflow-hidden font-inter bg-signup-form-bg bg-surface-page-bg">
        <div className="flex flex-1 min-h-0">
          {/* Left: form area */}
          <div className="flex-1 bg-signup-form-bg bg-surface-page-bg overflow-y-auto">
            <div className="flex flex-col items-center px-4 md:px-8 min-h-full py-8">
              <div className="w-full max-w-[620px] flex flex-col">
                {/* Logo + Theme Toggle */}
                <div className="flex items-center justify-between mb-10">
                  <img
                    src={fsvLogo}
                    alt="AchieveOS"
                    className="logo-light-mode h-[50px] w-[218px] max-w-full object-contain"
                  />
                  <img
                    src={fsvLogoDark}
                    alt="AchieveOS"
                    className="logo-dark-mode h-[50px] w-[218px] max-w-full object-contain"
                  />
                  <ThemeToggle />
                </div>

                <StepProgress
                  currentStep={4}
                  totalSteps={EXPERT_SIGNUP_STEP_LABELS.length}
                  stepLabels={EXPERT_SIGNUP_STEP_LABELS}
                />

                {/* Form card */}
                <div className="bg-theme-bg-surface border border-theme-border rounded-2xl px-4 md:px-10 pt-10 pb-10 mt-8">
                  {renderForm()}
                </div>
              </div>
            </div>
          </div>

          {/* Right: marketing panel */}
          <div className="hidden md:block">
            <MarketingPanel content={ADVISOR_STEP4_PANEL_CONTENT} />
          </div>
        </div>
      </div>
    </>
  );
};

export default SignUpStepFour;
